import { BLOCKS, DIRECTIONS, FLOW_WORDS, HEADERS, LINK_STYLES, NOTE_FILLERS, NOTE_PLACES, SECTION_WORDS, SEQ_WORDS, SHAPES, STYLE_KEYS } from './keywords';
import { closestWord, distance, safeCorrections } from './autocorrect';
import type {
  BlockKind,
  Diagnostic,
  Direction,
  EdgeKind,
  FlowEdge,
  FlowGroup,
  FlowModel,
  FlowNode,
  MessageKind,
  NodeShape,
  ParseResult,
  Participant,
  SeqEvent,
  SequenceModel,
  ShapeRegistry,
  StyleSpec,
} from './model';

/**
 * DSkhema parser (docs/ru/DSKHEMA.md §2): line based, never throws. Returns the best model it can
 * build plus diagnostics with 1-based positions, so the editor can keep showing a preview.
 */
export interface ParseOptions {
  /** Node forms contributed by modules (alias → object type). */
  shapes?: ShapeRegistry;
}

interface Line {
  /** 1-based number. */
  n: number;
  /** Text without the comment, right-trimmed. */
  text: string;
  /** Column (1-based) of the first non-space character. */
  indent: number;
}

const ID_RE = /^[\p{L}\p{N}_][\p{L}\p{N}_.-]*$/u;
const HEX_RE = /^#([0-9a-f]{3}|[0-9a-f]{4}|[0-9a-f]{6}|[0-9a-f]{8})$/i;

/** Cut `//` or `%%` comments (outside quotes, at line start or after a space). */
function stripComment(s: string): string {
  let q = false;
  for (let i = 0; i < s.length; i++) {
    const c = s[i];
    if (c === '"') q = !q;
    if (q) continue;
    if ((c === '/' && s[i + 1] === '/') || (c === '%' && s[i + 1] === '%')) {
      if (i === 0 || /\s/.test(s[i - 1])) return s.slice(0, i);
    }
  }
  return s;
}

function lines(source: string): Line[] {
  const out: Line[] = [];
  source.split(/\r?\n/).forEach((raw, i) => {
    const text = stripComment(raw).replace(/\s+$/, '');
    if (!text.trim()) return;
    out.push({ n: i + 1, text, indent: text.length - text.trimStart().length + 1 });
  });
  return out;
}

/** Index of `ch` outside double quotes, or -1. */
function indexOutside(s: string, ch: string, from = 0): number {
  let q = false;
  for (let i = from; i < s.length; i++) {
    if (s[i] === '"') q = !q;
    else if (!q && s.startsWith(ch, i)) return i;
  }
  return -1;
}

function unquote(s: string): string {
  const t = s.trim();
  return t.length >= 2 && t.startsWith('"') && t.endsWith('"') ? t.slice(1, -1) : t;
}

/** Label text: quotes removed, `\n` becomes a line break. */
function labelText(s: string): string {
  return unquote(s).replace(/\\n/g, '\n');
}

function firstWord(s: string): { word: string; rest: string } {
  const t = s.trim();
  const m = /^(\S+)\s*([\s\S]*)$/.exec(t);
  return m ? { word: m[1].toLowerCase(), rest: m[2] } : { word: '', rest: '' };
}

class Ctx {
  diagnostics: Diagnostic[] = [];
  /** Number of lines of the source (a fix past it appends). */
  lineCount = 0;
  /** Attach a suggested correction to the last diagnostic. */
  fix(line: number, col: number, end: number, text: string): void {
    const d = this.diagnostics[this.diagnostics.length - 1];
    if (d) d.fix = { line, col, end, text };
  }
  /** Suggest replacing `word` (found in the line) with the closest known word. */
  suggestWord(line: Line, word: string, candidates: Iterable<string>): void {
    const best = closestWord(word, candidates);
    if (!best) return;
    const i = line.text.toLowerCase().indexOf(word.toLowerCase());
    if (i >= 0) this.fix(line.n, i + 1, i + 1 + word.length, best);
  }
  /** A line with typographic arrows (`→`, `—>`): suggest the language's ones. */
  suggestArrows(line: Line): boolean {
    const edits = safeCorrections(line.text);
    if (!edits.length) return false;
    let s = line.text;
    for (const e of [...edits].reverse()) s = s.slice(0, e.from) + e.text + s.slice(e.to);
    this.fix(line.n, 1, line.text.length + 1, s);
    return true;
  }
  error(line: Line, code: string, params?: Record<string, string>, col?: number, end?: number): void {
    this.push('error', line, code, params, col, end);
  }
  warn(line: Line, code: string, params?: Record<string, string>, col?: number, end?: number): void {
    this.push('warning', line, code, params, col, end);
  }
  private push(severity: Diagnostic['severity'], line: Line, code: string, params?: Record<string, string>, col?: number, end?: number): void {
    const c = col ?? line.indent;
    this.diagnostics.push({ line: line.n, col: c, end: end ?? line.text.length + 1, severity, code, params });
  }
  /** Column of a substring inside the line (best effort). */
  colOf(line: Line, part: string): number {
    const i = line.text.indexOf(part.trim());
    return i >= 0 ? i + 1 : line.indent;
  }
}

/** `[цвет=#fff, пунктир]` → StyleSpec. */
function parseStyle(ctx: Ctx, line: Line, body: string): StyleSpec {
  const style: StyleSpec = {};
  for (const part of body.split(',')) {
    const p = part.trim();
    if (!p) continue;
    const eq = p.indexOf('=');
    const rawKey = (eq >= 0 ? p.slice(0, eq) : p).trim().toLowerCase();
    const value = eq >= 0 ? p.slice(eq + 1).trim() : '';
    const key = STYLE_KEYS.get(rawKey);
    if (!key) {
      ctx.warn(line, 'unknownStyleKey', { key: rawKey }, ctx.colOf(line, rawKey));
      ctx.suggestWord(line, rawKey, STYLE_KEYS.keys());
      continue;
    }
    if (key === 'dashed') style.dashed = value === '' || !/^(нет|no|false|0)$/i.test(value);
    else if (key === 'fine') style.fine = value === '' || !/^(нет|no|false|0)$/i.test(value);
    else if (key === 'flip') style.flip = value === '' || !/^(нет|no|false|0)$/i.test(value);
    else if (key === 'width') {
      const w = Number(value);
      if (Number.isFinite(w) && w >= 0 && w <= 50) style.width = w;
      else ctx.warn(line, 'badNumber', { value }, ctx.colOf(line, value));
    } else if (HEX_RE.test(value)) style[key] = value.toLowerCase();
    else ctx.warn(line, 'badColor', { value }, ctx.colOf(line, value || rawKey));
  }
  return style;
}

interface Trailing {
  core: string;
  shape?: NodeShape;
  style?: StyleSpec;
  classes: string[];
}

/** Peel `(form)`, `[style]` and `@class` off the end of a declaration or edge label. */
function peelTrailing(ctx: Ctx, line: Line, text: string, shapes: ShapeRegistry | undefined, allowShape: boolean): Trailing {
  let core = text.trim();
  const out: Trailing = { core, classes: [] };
  for (let guard = 0; guard < 8; guard++) {
    if (core.endsWith(']')) {
      const open = core.lastIndexOf('[');
      if (open < 0) break;
      out.style = { ...(out.style ?? {}), ...parseStyle(ctx, line, core.slice(open + 1, -1)) };
      core = core.slice(0, open).trim();
      continue;
    }
    if (allowShape && core.endsWith(')')) {
      const open = core.lastIndexOf('(');
      if (open < 0) break;
      const word = core.slice(open + 1, -1).trim();
      const shape = resolveShape(word, shapes);
      // "Save (draft)" stays a label: only known forms are taken
      if (!shape) break;
      out.shape = shape;
      core = core.slice(0, open).trim();
      continue;
    }
    const at = /(?:^|\s)@([\p{L}\p{N}_-]+)$/u.exec(core);
    if (at) {
      out.classes.unshift(at[1]);
      core = core.slice(0, at.index).trim();
      continue;
    }
    break;
  }
  out.core = core;
  return out;
}

export function resolveShape(word: string, shapes?: ShapeRegistry): NodeShape | null {
  const w = word.trim().toLowerCase();
  if (!w) return null;
  const builtin = SHAPES.get(w);
  if (builtin) return builtin;
  const fromModule = shapes?.get(w);
  if (fromModule) return `module:${fromModule}`;
  // a full module object type, e.g. org.vp.shapes:cube
  if (/^[a-z0-9]+(\.[a-z0-9-]+)+:[a-z][a-z0-9-]*$/.test(w)) return `module:${w}`;
  return null;
}

function validId(s: string): boolean {
  const t = s.trim();
  return (t.startsWith('"') && t.endsWith('"') && t.length > 2) || ID_RE.test(t);
}

// ---------------------------------------------------------------- flowcharts

const FLOW_OPS: [string, EdgeKind][] = [
  ['<->', 'both'],
  ['-->', 'arrow'],
  ['..>', 'dotted-arrow'],
  ['..', 'dotted'],
  ['=>', 'thick'],
  ['->', 'arrow'],
  ['--', 'line'],
];

/** Split `a -> b, c -> d` into id lists and operators (outside quotes). */
function splitEdges(s: string): { parts: string[]; ops: EdgeKind[] } | null {
  const parts: string[] = [];
  const ops: EdgeKind[] = [];
  let q = false;
  let start = 0;
  for (let i = 0; i < s.length; i++) {
    if (s[i] === '"') q = !q;
    if (q) continue;
    for (const [op, kind] of FLOW_OPS) {
      if (s.startsWith(op, i)) {
        parts.push(s.slice(start, i));
        ops.push(kind);
        i += op.length - 1;
        start = i + 1;
        break;
      }
    }
  }
  if (!ops.length) return null;
  parts.push(s.slice(start));
  return { parts, ops };
}

function parseFlow(ctx: Ctx, body: Line[], direction: FlowModel['direction'], links: FlowModel['links'], shapes?: ShapeRegistry): FlowModel {
  const nodes = new Map<string, FlowNode>();
  const declared = new Set<string>();
  const edges: FlowEdge[] = [];
  const groups: FlowGroup[] = [];
  const classes = new Map<string, StyleSpec>();
  const pendingClasses: { node: FlowNode; names: string[]; line: Line }[] = [];
  const stack: { id: string; line: Line }[] = [];
  const currentGroup = () => (stack.length ? stack[stack.length - 1].id : null);
  /** How often each node name is written, and where first (for "probably a typo" hints). */
  const uses = new Map<string, { n: number; line: Line; raw: string }>();

  const touch = (rawId: string, line: Line): FlowNode | null => {
    if (!validId(rawId)) {
      ctx.error(line, 'badId', { id: rawId.trim() }, ctx.colOf(line, rawId));
      return null;
    }
    const id = unquote(rawId);
    const u = uses.get(id);
    if (u) u.n++;
    else uses.set(id, { n: 1, line, raw: rawId.trim() });
    let node = nodes.get(id);
    if (!node) {
      node = { id, label: id.replace(/\\n/g, '\n'), shape: 'rect', style: {}, group: currentGroup(), line: line.n };
      nodes.set(id, node);
    } else if (!node.group && currentGroup()) node.group = currentGroup();
    return node;
  };

  for (const line of body) {
    const t = line.text.trim();
    if (t === '}') {
      if (!stack.pop()) ctx.error(line, 'unexpectedBrace');
      continue;
    }
    const { word, rest } = firstWord(t);
    const stmt = FLOW_WORDS.get(word);
    if (stmt === 'group') {
      let head = rest.trim();
      const hasBrace = head.endsWith('{');
      if (!hasBrace) {
        ctx.error(line, 'expectedBrace', undefined, line.text.length + 1, line.text.length + 2);
        ctx.fix(line.n, line.text.length + 1, line.text.length + 1, ' {');
      }
      else head = head.slice(0, -1).trim();
      const colon = indexOutside(head, ':');
      const rawId = colon >= 0 ? head.slice(0, colon).trim() : '';
      const title = labelText(colon >= 0 ? head.slice(colon + 1) : head) || `group${groups.length + 1}`;
      const id = rawId && validId(rawId) ? unquote(rawId) : title;
      groups.push({ id, title, parent: currentGroup(), line: line.n });
      if (hasBrace) stack.push({ id, line });
      continue;
    }
    if (stmt === 'class') {
      const tr = peelTrailing(ctx, line, rest, shapes, false);
      const name = tr.core.trim();
      if (!name) ctx.error(line, 'badId', { id: '' });
      else classes.set(name, tr.style ?? {});
      continue;
    }

    // edge statement: operators before the first ":" (label separator)
    const colon = indexOutside(t, ':');
    const head = colon >= 0 ? t.slice(0, colon) : t;
    const split = splitEdges(head);
    if (split) {
      const labelPart = colon >= 0 ? t.slice(colon + 1) : '';
      const tr = peelTrailing(ctx, line, labelPart, shapes, false);
      let parts = split.parts;
      let style = tr.style ?? {};
      // a style right after the last id: `a -> b [контур=#f00]`
      if (!labelPart) {
        const last = peelTrailing(ctx, line, parts[parts.length - 1], shapes, false);
        if (last.style) {
          style = last.style;
          parts = [...parts.slice(0, -1), last.core];
        }
      }
      const lists = parts.map((p) => p.split(',').map((x) => x.trim()));
      if (lists.some((l) => l.some((x) => !x))) {
        ctx.error(line, 'missingTarget');
        continue;
      }
      const resolved = lists.map((l) => l.map((x) => touch(x, line)).filter((x): x is FlowNode => !!x));
      for (let i = 0; i < split.ops.length; i++) {
        for (const a of resolved[i]) for (const b of resolved[i + 1]) edges.push({ from: a.id, to: b.id, kind: split.ops[i], label: labelText(tr.core), style, line: line.n });
      }
      continue;
    }

    // declaration `id: label (form) [style] @class`, or bare ids inside a group
    const tr = peelTrailing(ctx, line, t, shapes, true);
    const c = indexOutside(tr.core, ':');
    const rawId = c >= 0 ? tr.core.slice(0, c) : tr.core;
    if (c < 0 && rawId.includes(',')) {
      for (const id of rawId.split(',')) touch(id, line);
      continue;
    }
    if (c < 0 && !validId(rawId)) {
      ctx.error(line, 'unknownStatement', { text: t.length > 40 ? t.slice(0, 40) + '…' : t });
      if (!ctx.suggestArrows(line)) ctx.suggestWord(line, word, FLOW_WORDS.keys());
      continue;
    }
    const node = touch(rawId, line);
    if (!node) continue;
    if (declared.has(node.id) && (c >= 0 || tr.shape)) ctx.warn(line, 'duplicateNode', { id: node.id });
    declared.add(node.id);
    if (c >= 0) {
      const label = labelText(tr.core.slice(c + 1));
      if (label) node.label = label;
    }
    if (tr.shape) node.shape = tr.shape;
    if (tr.style) node.style = { ...node.style, ...tr.style };
    if (tr.classes.length) pendingClasses.push({ node, names: tr.classes, line });
    if (currentGroup() && !node.group) node.group = currentGroup();
  }
  for (const s of stack) {
    ctx.error(s.line, 'unclosedBlock', { name: s.id });
    ctx.fix(ctx.lineCount + 1, 1, 1, '\n' + ' '.repeat(Math.max(0, s.line.indent - 1)) + '}');
  }
  // a name written once that differs from a common one only by case or one letter: probably a typo
  for (const [id, u] of uses) {
    if (u.n !== 1 || declared.has(id)) continue;
    const low = id.toLowerCase();
    const similar = [...uses.keys()].filter((o) => {
      if (o === id) return false;
      const ol = o.toLowerCase();
      // the common spelling: written more often or declared; for case-only differences — the earlier one
      const common = (uses.get(o)?.n ?? 0) > 1 || declared.has(o) || (ol === low && (uses.get(o)?.line.n ?? Infinity) < u.line.n);
      return common && (ol === low || (Math.min(o.length, id.length) >= 6 && distance(low, ol, 1) <= 1));
    });
    if (similar.length !== 1) continue;
    const col = ctx.colOf(u.line, u.raw);
    ctx.warn(u.line, 'similarNode', { id, other: similar[0] }, col, col + u.raw.length);
    ctx.fix(u.line.n, col, col + u.raw.length, /^[\p{L}\p{N}_][\p{L}\p{N}_.-]*$/u.test(similar[0]) ? similar[0] : '"' + similar[0] + '"');
  }
  for (const p of pendingClasses) {
    let merged: StyleSpec = {};
    for (const name of p.names) {
      const cls = classes.get(name);
      if (!cls) {
        ctx.warn(p.line, 'unknownClass', { name });
        ctx.suggestWord(p.line, name, classes.keys());
      }
      else merged = { ...merged, ...cls };
    }
    // the node's own style wins over its classes
    p.node.style = { ...merged, ...p.node.style };
  }
  return { kind: 'flow', direction, links, nodes: [...nodes.values()], edges, groups };
}

// ---------------------------------------------------------------- sequences

const SEQ_OPS: [string, MessageKind][] = [
  ['-->', 'reply'],
  ['->>', 'async'],
  ['->', 'sync'],
];

function parseSequence(ctx: Ctx, body: Line[]): SequenceModel {
  const participants = new Map<string, Participant>();
  const declaredParticipants = new Set<string>();
  const events: SeqEvent[] = [];
  const stack: { kind: BlockKind; line: Line }[] = [];
  let autonumber = false;

  const who = (raw: string, line: Line): string | null => {
    const r = raw.trim();
    if (!validId(r)) {
      ctx.error(line, 'badId', { id: r }, ctx.colOf(line, r));
      return null;
    }
    const id = unquote(r);
    if (!participants.has(id)) participants.set(id, { id, label: id, actor: false, line: line.n });
    return id;
  };

  for (const line of body) {
    let t = line.text.trim();
    // `} иначе label {` / `} else {` / `}`
    if (t.startsWith('}')) {
      const after = t.slice(1).trim();
      if (!after) {
        if (!stack.pop()) ctx.error(line, 'unexpectedBrace');
        else events.push({ t: 'end', line: line.n });
        continue;
      }
      const { word, rest } = firstWord(after);
      if (SECTION_WORDS.has(word)) {
        const top = stack[stack.length - 1];
        if (!top || (top.kind !== 'alt' && top.kind !== 'par')) ctx.error(line, 'badSection', { word });
        let label = rest.trim();
        if (label.endsWith('{')) label = label.slice(0, -1).trim();
        else {
        ctx.error(line, 'expectedBrace', undefined, line.text.length + 1, line.text.length + 2);
        ctx.fix(line.n, line.text.length + 1, line.text.length + 1, ' {');
      }
        events.push({ t: 'section', label: labelText(label), line: line.n });
        continue;
      }
      ctx.error(line, 'unknownStatement', { text: t });
      continue;
    }
    const { word, rest } = firstWord(t);
    const block = BLOCKS.get(word);
    if (block) {
      let label = rest.trim();
      if (label.endsWith('{')) {
        label = label.slice(0, -1).trim();
        stack.push({ kind: block, line });
      } else {
        ctx.error(line, 'expectedBrace', undefined, line.text.length + 1, line.text.length + 2);
        ctx.fix(line.n, line.text.length + 1, line.text.length + 1, ' {');
      }
      events.push({ t: 'block', kind: block, label: labelText(label), line: line.n });
      continue;
    }
    const stmt = SEQ_WORDS.get(word);
    if (stmt === 'autonumber') {
      autonumber = true;
      continue;
    }
    if (stmt === 'participant' || stmt === 'actor') {
      const c = indexOutside(rest, ':');
      const rawId = c >= 0 ? rest.slice(0, c) : rest;
      if (!validId(rawId)) {
        ctx.error(line, 'badId', { id: rawId.trim() }, ctx.colOf(line, rawId));
        continue;
      }
      const id = unquote(rawId);
      const label = c >= 0 ? labelText(rest.slice(c + 1)) : id;
      if (declaredParticipants.has(id)) ctx.warn(line, 'duplicateNode', { id });
      declaredParticipants.add(id);
      // declared after its first use: the column keeps its place, the look is updated
      const existing = participants.get(id);
      participants.set(id, { id, label: label || id, actor: stmt === 'actor', line: existing?.line ?? line.n });
      continue;
    }
    if (stmt === 'activate' || stmt === 'deactivate') {
      const id = who(rest, line);
      if (id) events.push({ t: stmt, who: id, line: line.n });
      continue;
    }
    if (stmt === 'note') {
      const p = firstWord(rest);
      const place = NOTE_PLACES.get(p.word);
      if (!place) {
        ctx.error(line, 'badNote');
        continue;
      }
      let tail = p.rest;
      const f = firstWord(tail);
      if (NOTE_FILLERS.has(f.word)) tail = f.rest;
      const c = indexOutside(tail, ':');
      if (c < 0) {
        ctx.error(line, 'badNote');
        continue;
      }
      const ids = tail
        .slice(0, c)
        .split(',')
        .map((x) => who(x, line))
        .filter((x): x is string => !!x);
      if (!ids.length) continue;
      events.push({ t: 'note', place, who: ids.slice(0, place === 'over' ? 2 : 1), text: labelText(tail.slice(c + 1)), line: line.n });
      continue;
    }
    // message `a -> +b : text`
    const colon = indexOutside(t, ':');
    const head = colon >= 0 ? t.slice(0, colon) : t;
    let found: { at: number; op: string; kind: MessageKind } | null = null;
    for (let i = 0; i < head.length && !found; i++) {
      for (const [op, kind] of SEQ_OPS) {
        if (head.startsWith(op, i) && indexOutside(head, op) === i) {
          found = { at: i, op, kind };
          break;
        }
      }
    }
    if (!found) {
      ctx.error(line, 'unknownStatement', { text: t.length > 40 ? t.slice(0, 40) + '…' : t });
      if (!ctx.suggestArrows(line)) ctx.suggestWord(line, firstWord(t).word, [...SEQ_WORDS.keys(), ...BLOCKS.keys()]);
      continue;
    }
    let target = head.slice(found.at + found.op.length).trim();
    let activate = false;
    let deactivate = false;
    if (target.startsWith('+')) {
      activate = true;
      target = target.slice(1);
    } else if (target.startsWith('-')) {
      deactivate = true;
      target = target.slice(1);
    }
    const src = head.slice(0, found.at);
    if (!src.trim() || !target.trim()) {
      ctx.error(line, 'missingTarget');
      continue;
    }
    const from = who(src, line);
    const to = who(target, line);
    t = colon >= 0 ? t.slice(colon + 1) : '';
    if (from && to) events.push({ t: 'message', from, to, kind: found.kind, label: labelText(t), activate, deactivate, line: line.n });
  }
  for (const s of stack) {
    ctx.error(s.line, 'unclosedBlock', { name: s.kind });
    ctx.fix(ctx.lineCount + 1, 1, 1, '\n' + ' '.repeat(Math.max(0, s.line.indent - 1)) + '}');
  }
  return { kind: 'sequence', autonumber, participants: [...participants.values()], events };
}

// ---------------------------------------------------------------- entry

export function parse(source: string, opts: ParseOptions = {}): ParseResult {
  const ctx = new Ctx();
  ctx.lineCount = source.split('\n').length;
  const all = lines(source);
  if (!all.length) {
    ctx.diagnostics.push({ line: 1, col: 1, end: 1, severity: 'error', code: 'missingHeader' });
    return { model: null, diagnostics: ctx.diagnostics };
  }
  const [header, ...body] = all;
  const { word, rest } = firstWord(header.text);
  let kind = HEADERS.get(word);
  let guessed = false;
  if (!kind) {
    ctx.error(header, 'unknownHeader', { word });
    ctx.suggestWord(header, word, HEADERS.keys());
    // a misspelled header: check the rest as if it were the suggested one, so every problem shows at once
    const fix = ctx.diagnostics[ctx.diagnostics.length - 1]?.fix;
    kind = fix ? HEADERS.get(fix.text) : undefined;
    if (!kind) return { model: null, diagnostics: ctx.diagnostics };
    guessed = true;
  }
  if (kind === 'sequence') {
    if (rest.trim()) ctx.warn(header, 'unexpectedText', { text: rest.trim() }, ctx.colOf(header, rest));
    const seq = parseSequence(ctx, body);
    return { model: guessed ? null : seq, diagnostics: ctx.diagnostics };
  }
  // header words: a direction and/or a link style, in any order
  let direction: FlowModel['direction'] = 'down';
  let links: FlowModel['links'] = 'orthogonal';
  for (const w of rest.trim().split(/\s+/).filter(Boolean)) {
    const word = w.toLowerCase();
    const d = DIRECTIONS.get(word);
    const l = LINK_STYLES.get(word);
    if (d) direction = d;
    else if (l) links = l;
    else {
      ctx.warn(header, 'unknownDirection', { word: w }, ctx.colOf(header, w));
      ctx.suggestWord(header, w, [...DIRECTIONS.keys(), ...LINK_STYLES.keys()]);
    }
  }
  const flow = parseFlow(ctx, body, direction, links, opts.shapes);
  return { model: guessed ? null : flow, diagnostics: ctx.diagnostics };
}

/** Does this text start with a DSkhema header (used when pasting)? */
export function looksLikeDskhema(text: string): boolean {
  const first = lines(text)[0];
  return !!first && HEADERS.has(firstWord(first.text).word);
}

/** Direction of a flowchart source (`down` when the header names none); null for other kinds. */
export function flowDirection(source: string): Direction | null {
  const first = lines(source)[0];
  if (!first) return null;
  const { word, rest } = firstWord(first.text);
  if (HEADERS.get(word) !== 'flow') return null;
  let dir: Direction = 'down';
  for (const w of rest.trim().split(/\s+/)) dir = DIRECTIONS.get(w.toLowerCase()) ?? dir;
  return dir;
}

const DIRECTION_WORDS: Record<'ru' | 'en', Record<Direction, string>> = {
  ru: { down: 'вниз', right: 'вправо', up: 'вверх', left: 'влево' },
  en: { down: 'down', right: 'right', up: 'up', left: 'left' },
};

/**
 * The same flowchart source with another direction: only the header changes (its other words and
 * comment stay), the word is in the header's language. Other kinds of source come back unchanged.
 */
export function withFlowDirection(source: string, dir: Direction): string {
  const first = lines(source)[0];
  if (!first) return source;
  const { word, rest } = firstWord(first.text);
  if (HEADERS.get(word) !== 'flow') return source;
  const parts = source.split(/(\r?\n)/);
  const raw = parts[(first.n - 1) * 2];
  const head = first.text.trim();
  const lang = /[а-яё]/i.test(head) ? 'ru' : 'en';
  const others = rest.trim().split(/\s+/).filter((w) => w && !DIRECTIONS.has(w.toLowerCase()));
  const typed = head.split(/\s+/)[0];
  const indent = raw.slice(0, raw.length - raw.trimStart().length);
  parts[(first.n - 1) * 2] = indent + [typed, DIRECTION_WORDS[lang][dir], ...others].join(' ') + raw.slice(first.text.length);
  return parts.join('');
}
